import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, ChefHat, BellRing, Martini, ShoppingBasket, Sparkles,
    Anchor, Fish, Sailboat, Waves, Compass, LifeBuoy,
    Flag, Dumbbell, Leaf, Scissors,
    Baby, Heart, Car, Plane, Truck, Disc, PawPrint, ShoppingBag,
    Palette, Landmark, Soup, Mountain, Bike, Camera, Tent,
    Briefcase, Users, CalendarCheck, FileText, PartyPopper,
    MapPin, Network, MessageSquare, Award, GitBranch, Languages, Wand2,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { useLanguage } from "@/lib/LanguageContext";
import useSEO from "@/lib/useSEO";
import { gaConciergeView } from "@/lib/analytics";

/* =============================================================================
   IMAGERY — Cabo-authentic luxury lifestyle. Replace each URL with PKM photography
   once available (drop into /app/frontend/public/concierge/<filename>.jpg).
   Each constant carries a one-line creative direction.
   ============================================================================= */

// HERO_IMAGE — dramatic Cabo modern villa at twilight, ocean beyond.
const HERO_IMAGE = "https://images.unsplash.com/photo-1602343168117-bb8ffe3e2e9f?auto=format&fit=crop&w=2400&q=85";

// INTRO_IMAGE — refined indoor-outdoor Cabo terrace, suggesting bespoke service.
const INTRO_IMAGE = "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1800&q=85";

// DINING_IMAGE — luxury private chef preparing a course, plated detail.
const DINING_IMAGE = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1800&q=85";

// OCEAN_IMAGE — sailboat / yacht on the Sea of Cortez, painterly tones.
const OCEAN_IMAGE = "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?auto=format&fit=crop&w=1800&q=85";

// GOLF_IMAGE — Cabo championship fairway, ocean horizon line.
const GOLF_IMAGE = "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?auto=format&fit=crop&w=1800&q=85";

// WELLNESS_IMAGE — quiet spa moment, stones / oil / linen — luxurious.
const WELLNESS_IMAGE = "https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=1800&q=85";

// FAMILY_IMAGE — indoor-outdoor villa terrace, considered family stay.
const FAMILY_IMAGE = "https://images.unsplash.com/photo-1600573472550-8090b5e0745e?auto=format&fit=crop&w=1800&q=85";

// EXCLUSIVE_IMAGE — Baja desert / coastline drama for exclusive experiences.
const EXCLUSIVE_IMAGE = "https://images.unsplash.com/photo-1505228395891-9a51e7e86bf6?auto=format&fit=crop&w=1800&q=85";

// BUSINESS_IMAGE — refined executive workspace / villa office overlooking ocean.
const BUSINESS_IMAGE = "https://images.unsplash.com/photo-1600585152915-d208bec867a1?auto=format&fit=crop&w=1800&q=85";

// WHY_IMAGE — quietly elegant Palmilla-style infinity pool villa.
const WHY_IMAGE = "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1800&q=85";

/* =============================================================================
   KEYS & ICONS
   ============================================================================= */

const DINING_KEYS = ["d1", "d2", "d3", "d4", "d5"];
const DINING_ICONS = [ChefHat, BellRing, Martini, ShoppingBasket, Sparkles];

const OCEAN_KEYS = ["o1", "o2", "o3", "o4", "o5", "o6"];
const OCEAN_ICONS = [Anchor, Fish, Sailboat, Waves, Compass, LifeBuoy];

const GOLF_BULLET_KEYS = ["g1", "g2", "g3", "g4", "g5"];

const WELLNESS_KEYS = ["w1", "w2", "w3", "w4"];
const WELLNESS_ICONS = [Sparkles, Dumbbell, Leaf, Scissors];

const FAMILY_KEYS = ["f1", "f2", "f3", "f4", "f5", "f6", "f7", "f8"];
const FAMILY_ICONS = [Baby, Heart, Car, Plane, Truck, Disc, PawPrint, ShoppingBag];

const EXCLUSIVE_KEYS = ["ex1", "ex2", "ex3", "ex4", "ex5", "ex6", "ex7", "ex8", "ex9"];
const EXCLUSIVE_ICONS = [Palette, Landmark, Soup, Mountain, Bike, Tent, ShoppingBag, Camera, Flag];

const BUSINESS_KEYS = ["b1", "b2", "b3", "b4", "b5"];
const BUSINESS_ICONS = [Car, Users, CalendarCheck, FileText, PartyPopper];

const WHY_KEYS = ["why1", "why2", "why3", "why4", "why5", "why6", "why7", "why8"];
const WHY_ICONS = [MapPin, Network, Wand2, MessageSquare, Award, GitBranch, Languages, Sparkles];

/* =============================================================================
   SUB-COMPONENTS
   ============================================================================= */

function ServiceCard({ Icon, title, bullets, tone = "light", index }) {
    const isDark = tone === "dark";
    return (
        <Reveal
            delay={index * 60}
            className={`p-9 min-h-[280px] ${isDark ? "bg-navy" : "bg-pearl"}`}
            data-testid={`concierge-card-${index}`}
        >
            <Icon className="w-7 h-7 text-gold" strokeWidth={1.25} />
            <h3 className={`mt-7 font-serif text-xl leading-tight ${isDark ? "text-pearl" : "text-navy"}`}>{title}</h3>
            <span className="block w-7 h-px bg-gold my-4" />
            {bullets && bullets.length > 0 && (
                <ul className="space-y-1.5">
                    {bullets.map((b, i) => (
                        <li
                            key={i}
                            className={`text-sm leading-relaxed flex items-start gap-2 ${isDark ? "text-pearl/70" : "text-navy/70"}`}
                        >
                            <span className="text-gold mt-1.5 flex-shrink-0 w-1 h-1 rounded-full bg-gold" />
                            <span>{b}</span>
                        </li>
                    ))}
                </ul>
            )}
        </Reveal>
    );
}

function SimpleTile({ Icon, label, tone = "light", index }) {
    const isDark = tone === "dark";
    return (
        <Reveal
            delay={index * 50}
            className={`p-8 min-h-[160px] flex flex-col justify-between ${isDark ? "bg-navy" : "bg-pearl"}`}
            data-testid={`concierge-tile-${index}`}
        >
            <Icon className="w-6 h-6 text-gold" strokeWidth={1.25} />
            <h3 className={`font-serif text-lg leading-snug ${isDark ? "text-pearl" : "text-navy"}`}>{label}</h3>
        </Reveal>
    );
}

/* =============================================================================
   PAGE
   ============================================================================= */

export default function Concierge() {
    const { t, lang } = useLanguage();

    // Fire concierge_view exactly once per navigation.
    useEffect(() => { const t = setTimeout(() => gaConciergeView(), 200); return () => clearTimeout(t); }, []);

    useSEO({
        title: lang === "es"
            ? "Servicios de Concierge Privado Los Cabos | Pacific Key Management"
            : "Private Concierge Services Los Cabos | Pacific Key Management",
        description: lang === "es"
            ? "Chefs privados, charters de yate, golf, transporte, aprovisionamiento de villa y experiencias concierge personalizadas en Los Cabos."
            : "Private chefs, yacht charters, golf, transportation, villa pre-stocking and personalized concierge experiences throughout Los Cabos.",
        path: "/concierge",
        keywords: "Luxury Concierge Los Cabos, Private Chef Cabo, Yacht Charter Cabo, Golf Concierge Cabo, Villa Concierge Los Cabos, Family Concierge Cabo, Pacific Key Management",
        breadcrumbs: [
            { name: "Home", path: "/" },
            { name: "Concierge", path: "/concierge" },
        ],
        schema: {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Private Concierge",
            provider: { "@type": "Organization", name: "Pacific Key Management", url: "https://pacifickeymanagement.com" },
            areaServed: { "@type": "Place", name: "Los Cabos, Baja California Sur, Mexico" },
            url: "https://pacifickeymanagement.com/concierge",
        },
    });

    return (
        <div data-testid="concierge-page">
            {/* HERO */}
            <PageHero
                eyebrow={t("concierge.heroEyebrow")}
                title={<>{t("concierge.heroTitle1")} <span className="italic font-light">{t("concierge.heroTitle2")}</span></>}
                subtitle={t("concierge.heroSubtitle")}
                image={HERO_IMAGE}
            />
            <div className="bg-navy text-pearl py-10 sm:py-12 border-b border-pearl/10">
                <div className="container-luxe flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between">
                    <p className="text-pearl/85 max-w-2xl text-base sm:text-lg leading-relaxed italic font-light">
                        &ldquo;{t("concierge.introTagline")}&rdquo;
                    </p>
                    <Link to="/contact" className="btn-luxe-light flex-shrink-0" data-testid="concierge-hero-cta">
                        {t("concierge.heroCta")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Link>
                </div>
            </div>

            {/* SECTION 1 — Curated Experiences */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="concierge-intro-section">
                <div className="container-luxe grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
                    <Reveal className="lg:col-span-6 space-y-7">
                        <p className="overline text-gold">{t("concierge.introEyebrow")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-balance">{t("concierge.introTitle")}</h2>
                        <span className="gold-divider" />
                        <p className="text-navy/75 leading-relaxed text-lg">{t("concierge.introBody")}</p>
                    </Reveal>
                    <Reveal delay={120} className="lg:col-span-6">
                        <div className="aspect-[5/6] overflow-hidden">
                            <img src={INTRO_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 2 — Private Dining & Hospitality */}
            <section className="bg-sand/40 py-24 sm:py-32" data-testid="concierge-dining-section">
                <div className="container-luxe">
                    <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-end mb-16">
                        <Reveal className="lg:col-span-6">
                            <p className="overline text-gold">{t("concierge.diningEyebrow")}</p>
                            <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("concierge.diningTitle")}</h2>
                        </Reveal>
                        <Reveal delay={120} className="lg:col-span-6">
                            <div className="aspect-[16/10] overflow-hidden">
                                <img src={DINING_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        </Reveal>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-px bg-navy/10">
                        {DINING_KEYS.map((k, i) => (
                            <ServiceCard
                                key={k}
                                Icon={DINING_ICONS[i] || ChefHat}
                                title={t(`concierge.${k}.title`)}
                                bullets={t(`concierge.${k}.bullets`)}
                                tone="light"
                                index={i}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 3 — Ocean & Yacht */}
            <section className="bg-navy text-pearl py-24 sm:py-32" data-testid="concierge-ocean-section">
                <div className="container-luxe">
                    <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-center mb-20">
                        <Reveal className="lg:col-span-5">
                            <div className="aspect-[4/5] overflow-hidden">
                                <img src={OCEAN_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        </Reveal>
                        <Reveal delay={120} className="lg:col-span-7 space-y-6">
                            <p className="overline text-gold">{t("concierge.oceanEyebrow")}</p>
                            <h2 className="font-serif text-pearl text-4xl sm:text-5xl leading-tight">{t("concierge.oceanTitle")}</h2>
                            <span className="gold-divider" />
                            <p className="text-pearl/75 leading-relaxed max-w-xl">{t("concierge.oceanBody")}</p>
                        </Reveal>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-pearl/10">
                        {OCEAN_KEYS.map((k, i) => (
                            <ServiceCard
                                key={k}
                                Icon={OCEAN_ICONS[i] || Anchor}
                                title={t(`concierge.${k}.title`)}
                                bullets={t(`concierge.${k}.bullets`)}
                                tone="dark"
                                index={i}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 4 — Golf */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="concierge-golf-section">
                <div className="container-luxe grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
                    <Reveal className="lg:col-span-7 space-y-7">
                        <p className="overline text-gold">{t("concierge.golfEyebrow")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{t("concierge.golfTitle")}</h2>
                        <span className="gold-divider" />
                        <p className="text-navy/75 leading-relaxed">{t("concierge.golfBody")}</p>
                        <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-3 pt-4">
                            {GOLF_BULLET_KEYS.map((k, i) => (
                                <li key={k} className="flex items-center gap-4" data-testid={`concierge-golf-${i}`}>
                                    <span className="w-6 h-px bg-gold flex-shrink-0" />
                                    <span className="font-serif text-base text-navy">{t(`concierge.${k}`)}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal delay={120} className="lg:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden">
                            <img src={GOLF_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 5 — Wellness */}
            <section className="bg-sand/40 py-24 sm:py-32" data-testid="concierge-wellness-section">
                <div className="container-luxe">
                    <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-end mb-16">
                        <Reveal className="lg:col-span-7">
                            <p className="overline text-gold">{t("concierge.wellnessEyebrow")}</p>
                            <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("concierge.wellnessTitle")}</h2>
                        </Reveal>
                        <Reveal delay={120} className="lg:col-span-5">
                            <div className="aspect-[5/4] overflow-hidden">
                                <img src={WELLNESS_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        </Reveal>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10">
                        {WELLNESS_KEYS.map((k, i) => (
                            <ServiceCard
                                key={k}
                                Icon={WELLNESS_ICONS[i] || Sparkles}
                                title={t(`concierge.${k}.title`)}
                                bullets={t(`concierge.${k}.bullets`)}
                                tone="light"
                                index={i}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 6 — Family & Lifestyle */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="concierge-family-section">
                <div className="container-luxe">
                    <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-center mb-16">
                        <Reveal className="lg:col-span-5">
                            <div className="aspect-[4/5] overflow-hidden">
                                <img src={FAMILY_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        </Reveal>
                        <Reveal delay={120} className="lg:col-span-7 space-y-6">
                            <p className="overline text-gold">{t("concierge.familyEyebrow")}</p>
                            <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{t("concierge.familyTitle")}</h2>
                            <span className="gold-divider" />
                        </Reveal>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10">
                        {FAMILY_KEYS.map((k, i) => (
                            <SimpleTile
                                key={k}
                                Icon={FAMILY_ICONS[i] || Heart}
                                label={t(`concierge.${k}`)}
                                tone="light"
                                index={i}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 7 — Exclusive Los Cabos */}
            <section
                className="relative py-24 sm:py-32 bg-navy text-pearl overflow-hidden"
                data-testid="concierge-exclusive-section"
            >
                <div className="absolute inset-0">
                    <img src={EXCLUSIVE_IMAGE} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" />
                    <div className="absolute inset-0 bg-navy/80" />
                </div>
                <div className="relative container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("concierge.exclusiveEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-pearl text-4xl sm:text-5xl leading-tight">{t("concierge.exclusiveTitle")}</h2>
                        <span className="gold-divider mt-6" />
                        <p className="text-pearl/75 leading-relaxed mt-7 max-w-2xl">{t("concierge.exclusiveBody")}</p>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-7">
                        {EXCLUSIVE_KEYS.map((k, i) => {
                            const Icon = EXCLUSIVE_ICONS[i] || Compass;
                            return (
                                <Reveal
                                    key={k}
                                    delay={i * 50}
                                    className="flex items-center gap-5 border-t border-pearl/15 pt-5"
                                    data-testid={`concierge-exclusive-${i}`}
                                >
                                    <Icon className="w-5 h-5 text-gold flex-shrink-0" strokeWidth={1.25} />
                                    <span className="font-serif text-pearl text-lg">{t(`concierge.${k}`)}</span>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 8 — Business & Executive */}
            <section className="bg-sand/40 py-24 sm:py-32" data-testid="concierge-business-section">
                <div className="container-luxe grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
                    <Reveal className="lg:col-span-7 space-y-6">
                        <p className="overline text-gold">{t("concierge.businessEyebrow")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{t("concierge.businessTitle")}</h2>
                        <span className="gold-divider" />
                        <p className="text-navy/75 leading-relaxed">{t("concierge.businessBody")}</p>
                        <div className="grid sm:grid-cols-2 gap-px bg-navy/10 mt-4">
                            {BUSINESS_KEYS.map((k, i) => {
                                const Icon = BUSINESS_ICONS[i] || Briefcase;
                                return (
                                    <Reveal
                                        key={k}
                                        delay={i * 60}
                                        className="bg-pearl p-6 flex items-center gap-4"
                                        data-testid={`concierge-business-${i}`}
                                    >
                                        <Icon className="w-5 h-5 text-gold flex-shrink-0" strokeWidth={1.25} />
                                        <span className="font-serif text-base text-navy leading-snug">{t(`concierge.${k}`)}</span>
                                    </Reveal>
                                );
                            })}
                        </div>
                    </Reveal>
                    <Reveal delay={120} className="lg:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden">
                            <img src={BUSINESS_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 9 — Why Guests Choose PKM Concierge */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="concierge-why-section">
                <div className="container-luxe grid lg:grid-cols-12 gap-14 lg:gap-20 items-start">
                    <Reveal className="lg:col-span-5 lg:sticky lg:top-32 space-y-6">
                        <p className="overline text-gold">{t("concierge.whyEyebrow")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{t("concierge.whyTitle")}</h2>
                        <span className="gold-divider" />
                        <div className="aspect-[4/5] overflow-hidden mt-8">
                            <img src={WHY_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                    </Reveal>
                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-x-10 gap-y-12">
                        {WHY_KEYS.map((k, i) => {
                            const Icon = WHY_ICONS[i] || Sparkles;
                            return (
                                <Reveal key={k} delay={i * 70} data-testid={`concierge-why-${i}`}>
                                    <Icon className="w-6 h-6 text-gold mb-4" strokeWidth={1.25} />
                                    <h3 className="font-serif text-xl mb-2">{t(`concierge.${k}.title`)}</h3>
                                    <span className="block w-7 h-px bg-gold mb-3" />
                                    <p className="text-sm text-navy/70 leading-relaxed">{t(`concierge.${k}.body`)}</p>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-navy text-pearl py-24 sm:py-32 text-center" data-testid="concierge-cta-section">
                <div className="container-luxe max-w-2xl mx-auto space-y-7">
                    <p className="overline text-gold">{t("concierge.outroEyebrow")}</p>
                    <h2 className="font-serif text-pearl text-4xl sm:text-5xl leading-tight">{t("concierge.outroTitle")}</h2>
                    <p className="text-pearl/75 leading-relaxed">{t("concierge.outroBody")}</p>
                    <Link to="/contact" className="btn-luxe-light" data-testid="concierge-cta-button">
                        {t("concierge.outroCta")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
